import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs';
import { TaskDialogService } from '../../taskbook-dialog.service';
import { TaskbookService } from "../../taskbook.service";
import { TaskLabel } from "../../taskbook.types";

@Component({
    selector: 'task-label-popover',
    templateUrl: './task-label-popover.component.html',
    styleUrls: ['./task-label-popover.component.scss', './../../taskbook-triage-actions/taskbook-triage-actions.component.scss', './../../task/task.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      TranslateModule
    ]
})
export class TaskLabelPopoverComponent implements OnInit {
    protected availableLabels: TaskLabel[] = [];

    @Input() context: string;
    @Input() selectedLabels: TaskLabel[];
    @Output() labelsChange = new EventEmitter<TaskLabel[]>();
    @Output() apply = new EventEmitter<TaskLabel[]>();
  
    protected searchCtrl = new FormControl<string>('');
    protected filteredLabels: TaskLabel[] = [];

    constructor(
      private taskDialogService: TaskDialogService,
      private taskbookService: TaskbookService,
      private cdr: ChangeDetectorRef,
    ) {}
  
    ngOnInit(): void {
      this.loadLabels();

      this.searchCtrl.valueChanges.pipe(
          debounceTime(400),
          distinctUntilChanged(),
          tap(() => {
              const search_terms = this.searchCtrl.getRawValue();
              this.filteredLabels = this.search(search_terms);
          }),
      ).subscribe();
    }

    loadLabels(){
      this.taskbookService.availableLabels().pipe(
        tap(availableLabels => {
            this.availableLabels = availableLabels;
            this.filteredLabels = availableLabels;
            this.cdr.detectChanges();
        }),
       ).subscribe();
    }

    search(term: string): TaskLabel[] {
        const allLabels = this.availableLabels;
        return allLabels.filter(label => label.name.toLowerCase().includes(term.toLowerCase()));
    }
    async enterSearch(): Promise<void> {
      if(this.filteredLabels.length === 0){
        await this.addLabel();
      }else{
        this.selectedLabels = this.filteredLabels;
      }
    }

    setSelectedClass(label: TaskLabel): string {
      return this.selectedLabels.filter(l => l.id === label.id).length > 0 ? 'selected' : '';
    }

    applyLabels(selectedLabels: TaskLabel[]): void {
      this.apply.emit(selectedLabels);
      this.selectedLabels = [];
    }
  
    async addLabel() {
        const labelName = this.searchCtrl.getRawValue();
        if (this.availableLabels.map(label => label.name).indexOf(labelName) === -1) {
            //open dialog
            const newLabel = {
                name:  labelName,
                color: null,
                id: null
            };
            this.openLabelDialog(newLabel, 'create');
        }
    }

    async openLabelDialog(label: TaskLabel, mode) {
      const labelDialogRef = await this.taskDialogService.createOrEditTaskLabel(label, mode);
      labelDialogRef.beforeClosed().pipe(
          tap(label => {
            if (label && mode === 'create') {
              this.availableLabels.push(label);
              this.selectedLabels.push(label);
              this.cdr.detectChanges();
            }
            if (label && mode === 'edit') {
                this.taskbookService.editLabel(label).pipe(
                  tap(data => {
                      this.loadLabels();
                      this.cdr.detectChanges();
                  })
                ).subscribe();
            }
          }),
          takeUntil(labelDialogRef.afterClosed())
      ).subscribe();
    }

    editLabel(label: TaskLabel): void {
      this.openLabelDialog(label, 'edit');
    }

    deleteLabel(label: TaskLabel): void {
      this.taskbookService.deleteLabel(label).pipe(
        tap(data => {
            this.loadLabels();
        })
      ).subscribe();
    }
  
    toggleLabel(label: TaskLabel): void {
      if (this.selectedLabels.filter(l => l.id === label.id).length > 0) {
        this.selectedLabels = this.selectedLabels.filter(l => l.id !== label.id);
      } else {
        this.selectedLabels.push(label);
      }
      this.labelsChange.emit(this.selectedLabels);
    }
  }

